import {
    ComplianceBlockActiveRuleDto,
    PartyComplianceCheckDto,
    PartyRiskFactorDto,
    RiskProfileType,
    VerificationStatusType,
} from './compliance.ts';
import { CustomFieldDto } from './customFields';
import { Employee } from './employees.ts';
import { SelectType } from './forms';
import { PartyGroupMin } from './groups';
import { CustomizationDto } from './invoices';
import { CreateEntityFieldChangeReasonDto } from './matters';
import { OfficeMin } from './offices';
import { MinDto, NamedEntityMinDto, NumericIdPrimKeyDto } from './other';
import { TeamMinDto } from './teams';

export type EinvoiceOperatorType = 'FINVOICE' | 'PEPPOL';

export interface PartyFlags {
    deceased?: boolean;
}

export enum BlockHistoryField {
    // Party fields (legacy aggregate — kept so historical audit rows render)
    IS_ADDING_TIMECARDS_BLOCKED = 'IS_ADDING_TIMECARDS_BLOCKED',
    IS_ADDING_NEW_CASES_BLOCKED = 'IS_ADDING_NEW_CASES_BLOCKED',
    IS_EXEMPT_FROM_AUTOBLOCKING = 'IS_EXEMPT_FROM_AUTOBLOCKING',
    DEBT_EXTENSION_DATE = 'DEBT_EXTENSION_DATE',
    IS_ADDING_NEW_CASES_BLOCKED_MANUAL = 'IS_ADDING_NEW_CASES_BLOCKED_MANUAL',
    IS_ADDING_TIMECARDS_BLOCKED_MANUAL = 'IS_ADDING_TIMECARDS_BLOCKED_MANUAL',
    IS_COMPLIANCE_CASES_BLOCK_IGNORED = 'IS_COMPLIANCE_CASES_BLOCK_IGNORED',
    IS_COMPLIANCE_TIMECARDS_BLOCK_IGNORED = 'IS_COMPLIANCE_TIMECARDS_BLOCK_IGNORED',

    // Party fields (v3 — per-row party_block audit; one enum value per source/axis)
    IS_ADDING_AML_TIMECARDS_BLOCKED = 'IS_ADDING_AML_TIMECARDS_BLOCKED',
    PARTY_BLOCK_MANUAL_CASES = 'PARTY_BLOCK_MANUAL_CASES',
    PARTY_BLOCK_MANUAL_TIMECARDS = 'PARTY_BLOCK_MANUAL_TIMECARDS',
    PARTY_BLOCK_COMPLIANCE_CASES = 'PARTY_BLOCK_COMPLIANCE_CASES',
    PARTY_BLOCK_COMPLIANCE_TIMECARDS = 'PARTY_BLOCK_COMPLIANCE_TIMECARDS',
    PARTY_BLOCK_COMPLIANCE_AML_TIMECARDS = 'PARTY_BLOCK_COMPLIANCE_AML_TIMECARDS',
    PARTY_BLOCK_DEBT_TIMECARDS = 'PARTY_BLOCK_DEBT_TIMECARDS',

    // Matter fields
    MATTER_BLOCKING_POLICY_TYPE = 'MATTER_BLOCKING_POLICY_TYPE',
    MATTER_AML_BLOCKING_EXEMPT = 'MATTER_AML_BLOCKING_EXEMPT',
}

export type BlockSourceType = 'MANUAL' | 'COMPLIANCE' | 'DEBT';
export type BlockedActionTypeCode = 'CASES' | 'TIMECARDS' | 'AML_TIMECARDS';
export type ManualBlockAxis = Extract<BlockedActionTypeCode, 'CASES' | 'TIMECARDS'>;

export interface PartyBlockDto {
    id: number;
    sourceType: BlockSourceType;
    blockedActionTypeCode: BlockedActionTypeCode;
    blockedFrom: string | null;
    blockedUntil: string | null;
    gracePeriodDays: number | null;
    isIgnored: boolean;
}

// Mirrors portal-be PartyDto.PartyBlockWriteDto. The FE submits the FULL desired list of
// party_block rows on PUT /v1/parties/{id}; the BE diffs against current rows and synthesises
// CREATE/UPDATE/DELETE per source-type restrictions (MANUAL rows are client-mutable; COMPLIANCE
// and DEBT rows are server-managed and only their isIgnored/blockedFrom can be tweaked).
export interface PartyBlockWriteDto {
    id?: number; // present for existing rows; absent for new MANUAL row creation
    sourceType: BlockSourceType;
    blockedActionTypeCode: BlockedActionTypeCode;
    isIgnored: boolean;
    blockedFrom: string | null;
    blockedUntil: string | null;
    changeReason?: string | null;
}

// In-form shape — fixed schema (NOT keyed by row id) so every path that any control watches
// exists in defaultValues at form mount. RHF v7's useWatch is unreliable for paths that didn't
// exist at init; this design sidesteps that entirely by keeping all paths stable.
//
// The wire payload (PartyBlockWriteDto[]) is computed at submit time from this state plus the
// contact's current blocks (see buildBlocksPayload in ContactPageForm).
export interface ManualBlockFormState {
    enabled: boolean;
    changeReason: string;
}

export interface OverrideFormState {
    isIgnored: boolean;
    blockedFrom: string | null;
    changeReason: string;
}

export interface PartyBlockingFormState {
    manualCases: ManualBlockFormState;
    manualTimecards: ManualBlockFormState;
    // Pre-emptive DEBT/TIMECARDS exemption for clients with no debt-triggered DEBT row yet
    // (typical: partner law firms whose debts belong to *their* clients, not ours). Uses the same
    // shape as `OverrideFormState` so the same UI controls (indefinitely / until-date) work — at
    // save time buildBlocksPayload emits a no-id DEBT write carrying isIgnored/blockedFrom. Once
    // a real DEBT row exists, the override path under `overrides` takes over.
    debtTimecardsExempt: OverrideFormState;
    // Pre-emptive COMPLIANCE exemption per axis, analogous to debtTimecardsExempt. Emitted at
    // save time as a no-id COMPLIANCE write. Once a real COMPLIANCE row exists for the axis,
    // the override path under `overrides` takes over.
    complianceCasesExempt: OverrideFormState;
    complianceTimecardsExempt: OverrideFormState;
    complianceAmlTimecardsExempt: OverrideFormState;
    // Keyed by `r{blockId}` for each existing COMPLIANCE/DEBT row. Populated from contact.blocks
    // at form init so every row's path is in defaultValues.
    overrides: Record<string, OverrideFormState>;
}

interface EmployerExportDto {
    officialName?: string;
    segmentTypeCode?: string;
    serviceLevelTypeCode?: string;
}

export interface ContactListExportItem {
    id: number;
    clientNumber?: string | null;
    name: string;
    regNrOrIdCode?: string | null;
    position?: string | null;
    partyGroups?: PartyGroupMin[];
    businessAreas?: BusinessAreaDto[];
    primaryContact?: ContactMinDto;
    managers?: PartyToEmployeeRelationDto[];
    partners?: PartyToEmployeeRelationDto[];
    segmentTypeCode?: string;
    serviceLevelTypeCode?: string;
    lastBdEffort?: string;
    lastHCSBdEffort?: string;
    servedByOffices?: OfficeMin[];
    servedByPracticeGroups?: TeamMinDto[];
    servedBySectorGroups?: TeamMinDto[];
    mainEmail?: string;
    mainMobile?: string;
    employedByCompanies?: EmployerExportDto[];
}

interface Contact {
    id: number;
    partyType: PartyType;
    relationshipTypeCode: RelationshipTypeCode;
    name: string;
    position: string | null;
    mainEmail: string | null;
    mainPhone: string | null;
    mainMobile: string | null;
    statusCode: string;
    clientReference: string | null;
    segmentTypeCode: string | null;
    serviceLevelTypeCode: string | null;
    partyNumber: string | null;
    clientSequence: number | null;
    clientSince: string | null;
    paymentTermTypeCode: string | null;
    personTitleLabel: string | null;
    personTitleText: string | null;
    displayedPersonTitle: string | null;
    preferredLanguageCode: string | null;
    preferredLanguageLabel: string | null;
    primaryContactId: string | null;
    primaryContactLabel: string | null;
    invoicingIntervalTypeCode: string | null;
    invoicingPaymentDays: number;
    preferredBankAccount: MinDto | null;
    billingNotes: string | null;
    invoicingInfo: string | null;
    debtNotes: string | null;
    debtExplanation: string | null;
    debtHistory: string | null;
    notes: string | null;
    coiNotes: string | null;
    lastBdEffort: string | null;
    lastHCSBdEffort: string | null;
    contacts: ContactRelationDto[] | null;
    relations: PartyToPartyRelationDto[] | null;
    relationsValues: SelectType[] | null;
    relatesTo: PartyToPartyRelationDto[] | null;
    relatesToValues: SelectType[] | null;
    businessAreas: BusinessAreaDto[];
    relatedKeywords: RelatedKeywordDto[];
    // employees: PartyToEmployeeRelationDto[] | null;
    managers: PartyToEmployeeRelationDto[] | null;
    partners: PartyToEmployeeRelationDto[] | null;
    administrators: PartyToEmployeeRelationDto[] | null;
    businessContacts: PartyToEmployeeRelationDto[] | null;
    paymentNotificationReceivers?: PartyToEmployeeRelationDto[] | null;
    billingAddress: AddressDto | null;
    billingLocalAddress: AddressDto | null;
    createdAt?: Date;
    createdBy?: string;
    lastModifiedAt?: Date;
    lastModifiedBy?: string;
    createdByEmployee?: { id: number; name: string };
    changedByEmployee?: { id: number; name: string };
    deletedAt?: string | null;
    isDirectlyConnectedToMatterAsClient?: boolean;
    isEditable: boolean;
    isDeletable: boolean;
    flags?: PartyFlags;
    partyGroups?: PartyGroupMin[];
    customFields?: CustomFieldDto[];
    payerParty: ContactMinDto | null;
    useJointInvoicing: boolean | null;
    isInternalClient: boolean | null;
    issueInvoicesWithCurrentDate: boolean | null;
    primaryRecipientParty: ContactMinDto | null;
    primaryRecipientAdditionalEmails: string | null;
    secondaryRecipientAdditionalEmails: string | null;
    invoiceArchiveEmail: string | null;
    invoiceTemplateId: number | null;
    invoiceSpecTemplateId: number | null;
    isSpecIncludedInInvoice: boolean | null;
    invoiceCustomization: CustomizationDto | null;
    accountingRevenueTypeCode: string | null;
    servedByPracticeGroups?: TeamMinDto[];
    servedBySectorGroups?: TeamMinDto[];
    servedByOffices?: OfficeMin[];
    relatedMatterCount?: number;
    revenueStatistics?: PartyRevenueStatistics;
    riskFactors?: PartyRiskFactorDto[];
    complianceChecks?: PartyComplianceCheckDto[];
    complianceVerificationStatusTypeCode?: VerificationStatusType;
    complianceVerificationNotes: string | null;
    complianceResponsibilityTypeCode?: string;
    riskProfileTypeCode?: RiskProfileType;
    riskProfileNotes?: string;
    invoicingDeadlineDay: number | null;
    externalBillingInfo: string | null;
    isPersonaNonGrata: boolean;
    preferredMailingLanguageTypeCode: string | null;
    countries?: PartyCountryDto[];
    mailingPreferences?: PartyMailingPreferenceDto[];
    marketingAreaTypes?: PartyMarketingAreaTypeDto[];
    /** A person's only. */
    areaOfResponsibilityTypes?: PartyAreaOfResponsibilityTypeDto[];
    cooperationAreaTypes?: PartyCooperationAreaTypeDto[];
    isServiceAgreementNotNeeded: boolean | null;
    isUboNotNeeded: boolean | null;
    // Blocking — cached aggregates (OR across all enforcing party_block rows on each axis)
    isAddingNewCasesBlocked: boolean;
    isAddingTimecardsBlocked: boolean;
    isAddingAmlTimecardsBlocked: boolean;
    // Live, grace-aware AML-axis block status (BE-computed). Drives the AML axis chip, source row,
    // and compliance-banner chip. Distinct from the grace-blind cached isAddingAmlTimecardsBlocked.
    isAmlTimecardsBlockActive: boolean;
    complianceBlockReason: string | null;
    // Blocking — per-row breakdown (replaces the legacy per-source booleans + ignore fields)
    blocks: PartyBlockDto[];
    complianceBlockActiveRules?: ComplianceBlockActiveRuleDto[];
    blockingHistory?: string;
    dmsFolderId?: string | null;
}

export interface PartyRevenueStatistics {
    revenueTotalLastQuarter3y?: number;
    revenueYtd?: number;
    revenueYtdM1?: number;
    averageRateFy?: number;
    averageRateFyM1?: number;
    averageRateFyM2?: number;
    partyClientIds?: number[];
    baseCurrencyTypeCode?: string;
    outstandingBalance?: number;
    overdueAmount?: number;
}

export enum PartyGroupingStrategy {
    COMBINED = 'COMBINED',
    PARTIES_FIRST = 'PARTIES_FIRST',
    RELATIONSHIPS_FIRST = 'RELATIONSHIPS_FIRST',
}

export enum PartyType {
    PERSON = 'PERSON',
    COMPANY = 'COMPANY',
}

export enum RelationshipTypeCode {
    CLIENT = 'CLIENT',
    NON_CLIENT = 'NON-CLIENT',
    LEAD = 'LEAD',
}

export enum PartyRelationshipTypeCode {
    EMPLOYEE = 'EMPLOYEE',
    PRIMARY_CONTACT = 'PRIMARY_CONTACT',
    SECONDARY_RECIPIENT = 'SECONDARY_RECIPIENT',
}

interface ContactDataPersonDto extends Contact {
    personalCode: string | null;
    personTitleCode: string | null;
    linkedinUrl: string | null;
    homeAddress: AddressDto | null;
    homeLocalAddress: AddressDto | null;
    correspondenceAddress: AddressDto | null;
    firstName: string;
    lastName: string;
    localFirstName: string | null;
    localLastName: string | null;
    isPoliticallyExposedPerson?: boolean;
    identityDocumentNumber: string | null;
    identityDocumentDateOfIssue: string | null;
    identityDocumentDateOfExpiry: string | null;
    identityDocumentIssuingCountryCode: string | null;
    identityDocumentIssuingAuthority: string | null;
    uboForCompanies?: { id: number; name: string }[];
}

interface ContactDataCompanyDto extends Contact {
    officialName: string;
    officialLocalName: string | null;
    regCode: string | null;
    isVatLiable: boolean | null;
    vatRegNum: string | null;
    vatCountryCode: string | null;
    website: string | null;
    einvoiceAddress: string | null;
    einvoiceOperatorCode: string | null;
    einvoiceOperatorType: EinvoiceOperatorType | null;
    registrationAddress: AddressDto | null;
    registrationLocalAddress: AddressDto | null;
    visitingAddress: AddressDto | null;
    correspondenceAddress: AddressDto | null;
}

export interface ContactDto extends ContactDataPersonDto, ContactDataCompanyDto {
    /** Saved before its required fields were filled: no number yet, open only to its owner and those it is shared with. */
    isDraft?: boolean;
    timecardCount?: number;
    isTimecardAddingAutoBlocked?: boolean;
    dmsLink: string | null;
    opponentCaseState?: OpponentCaseState | null;
}

/** The strongest state of the cases a contact stands on as an opponent; absent when it opposes none. */
export type OpponentCaseState = 'IN_PROGRESS' | 'COMPLETED';

export interface ContactListItem {
    id: number;
    /** Listed with `includeDrafts` only: see {@link ContactDto.isDraft}. */
    isDraft?: boolean;
    name: string;
    partyType: PartyType;
    officialName: string;
    firstName?: string;
    lastName?: string;
    personalCode: string;
    regCode: string;
    relationshipTypeCode: RelationshipTypeCode;
    mainEmail: string | null;
    mainPhone: string | null;
    mainMobile: string | null;
    preferredLanguageCode: string | null;
    statusCode: string;
    clientReference: string | null;
    segmentTypeCode: string | null;
    serviceLevelTypeCode: string | null;
    relations: PartyToPartyRelationDto[] | null;
    relatesTo: PartyToPartyRelationDto[] | null;
    deletedAt: string;
    businessAreas: BusinessAreaDto[];
    clientSequence: number;
    clientSince?: string | null;
    partyNumber?: string | null;
    managers: PartyToEmployeeRelationDto[];
    partners: PartyToEmployeeRelationDto[];
    businessContacts: PartyToEmployeeRelationDto[] | null;
    isTimecardAddingAutoBlocked?: boolean;
    lastBdEffort: string | null;
    lastHCSBdEffort: string | null;
    homeAddress: AddressDto | null;
    registrationAddress: AddressDto | null;
    visitingAddress: AddressDto | null;
    correspondenceAddress: AddressDto | null;
    billingAddress: AddressDto | null;
    dmsLink: string | null;
    position: string | null;

    isEditable: boolean;
    complianceVerificationStatusTypeCode?: VerificationStatusType;
    flags?: PartyFlags;
    opponentCaseState?: OpponentCaseState | null;
}

interface ContactRelationDto {
    contactTypeCode: string;
    value: string;
}

export interface PartyToPartyRelationDto {
    otherParty: Partial<ContactMinDto> & { id: number; name: string };
    partyRelationshipTypeCode: PartyRelationshipTypeCode;
}

export interface PartyToEmployeeRelationDto {
    employee: {
        id: number;
        name: string;
        firstName?: string;
        lastName?: string;
        workEmailAddress?: string;
        personalIdCode?: string;
        isActive?: boolean;
        deletedAt?: string | null;
    };
    partyEmployeeTypeCode: string;
}

interface PartyToEmployeeRelationCreateOrUpdateDto {
    employee: Pick<Employee, 'id'>;
    partyEmployeeTypeCode: string;
}

export interface BusinessAreaDto {
    id?: number;
    businessAreaCode: string;
}

export interface RelatedKeywordDto {
    keywordTypeCode: string;
    notes?: string;
}

export interface AddressDto {
    id?: number | null;
    address1: string;
    address2: string | null;
    cityName: string;
    countryCode: string;
    zipCode: string | null;
    customFields?: CustomFieldDto[];
    additionalInformation: string | null;
}

export interface AddressForm {
    address1: string;
    address2: string | null;
    cityName: string;
    countryCode: { label: string; value: string } | null;
    zipCode: string | null;
    customFields?: CustomFieldDto[];
    additionalInformation: string | null;
}

export interface ContactMinDto {
    id: number;
    name: string;
    officialName: string | null;
    firstName?: string;
    lastName?: string;
    mainEmail: string | null;
    partyType: PartyType;
    relationshipTypeCode: RelationshipTypeCode;
    preferredLanguageCode: string | null;
    deletedAt?: string | null;
    isVatLiable?: boolean;
    vatRegNum?: string;
    position?: string;
    externalBillingInfo: string | null;
    invoicingDeadlineDay: number | null;
    isPersonaNonGrata: boolean;
    opponentCaseState?: OpponentCaseState | null;
    isAddingTimecardsBlocked: boolean;
    partyNumber?: string | null;
    clientSequence?: number | null;
    clientSince?: string | null;
    flags?: PartyFlags;
}

export interface ContactMinDtoWithRelationsDto extends ContactMinDto {
    relations?: PartyToPartyRelationDto[];
    relatesTo?: PartyToPartyRelationDto[];
}

export interface ContactMinDtoWithAddresses extends ContactMinDto {
    homeAddress?: AddressDto | null;
    homeLocalAddress?: AddressDto | null;
    registrationAddress?: AddressDto | null;
    registrationLocalAddress?: AddressDto | null;
    visitingAddress?: AddressDto | null;
    correspondenceAddress?: AddressDto | null;
    billingAddress?: AddressDto | null;
    billingLocalAddress?: AddressDto | null;
    personalCode?: string | null;
    regCode?: string | null;
    mainMobile?: string | null;
    relations?: PartyToPartyRelationDto[] | null;
    relatesTo?: PartyToPartyRelationDto[] | null;
    isEditable?: boolean;
}

export interface PartyMinDtoWithRelationships extends NamedEntityMinDto {
    relationshipManagers?: NamedEntityMinDto[];
    relationshipPartners?: NamedEntityMinDto[];
}

interface ContactCreateOrUpdateDto {
    id?: number;
    /** Sent by the quick add-contact window: the backend holds it only to the required fields that window shows. */
    quickAdd?: boolean;
    partyType: PartyType;
    relationshipTypeCode: RelationshipTypeCode;
    name: string;
    mainEmail: string | null;
    mainPhone: string | null;
    mainMobile: string | null;
    statusCode: string;
    clientReference: string | null;
    preferredLanguageCode: string | null;
    billingNotes: string | null;
    invoicingInfo: string | null;
    debtNotes: string | null;
    debtExplanation: string | null;
    debtHistory: string | null;
    notes: string | null;
    coiNotes: string | null;
    contacts: ContactRelationDto[] | null;
    relations: PartyToPartyRelationDto[] | null;
    relatesTo: PartyToPartyRelationDto[] | null;
    businessAreas: BusinessAreaDto[];
    relatedKeywords: RelatedKeywordDto[];
    managers: PartyToEmployeeRelationCreateOrUpdateDto[] | null;
    partners: PartyToEmployeeRelationCreateOrUpdateDto[] | null;
    administrators: PartyToEmployeeRelationCreateOrUpdateDto[] | null;
    businessContacts: PartyToEmployeeRelationCreateOrUpdateDto[] | null;
    paymentNotificationReceivers: PartyToEmployeeRelationCreateOrUpdateDto[] | null;
    billingAddress: AddressDto | null;
    billingLocalAddress: AddressDto | null;
    paymentTermTypeCode: string | null;
    invoicingIntervalTypeCode: string | null;
    invoicingPaymentDays: number | null;
    preferredBankAccountId: number | null;
    payerParty: { name: string; id: number } | null;
    useJointInvoicing: boolean | null;
    isInternalClient: boolean | null;
    issueInvoicesWithCurrentDate: boolean | null;
    primaryRecipientParty: NamedEntityMinDto | null;
    primaryRecipientAdditionalEmails: string | null;
    secondaryRecipientParties?: NumericIdPrimKeyDto[];
    secondaryRecipientAdditionalEmails: string | null;
    invoiceArchiveEmail: string | null;
    invoiceTemplateId: number | null;
    invoiceSpecTemplateId: number | null;
    isSpecIncludedInInvoice: boolean | null;
    invoiceCustomization: CustomizationDto | null;
    dmsLink: string | null;
    segmentTypeCode: string | null;
    serviceLevelTypeCode: string | null;
    invoicingDeadlineDay: number | null;
    externalBillingInfo: string | null;
    isPersonaNonGrata: boolean;
    riskProfileTypeCode: string | null;
    riskProfileNotes: string | null;
    complianceVerificationStatusTypeCode: string | null;
    complianceVerificationNotes: string | null;
    preferredMailingLanguageTypeCode: string | null;
    countries?: CreateOrUpdatePartyCountryDto[];
    mailingPreferences?: CreateOrUpdatePartyMailingPreferenceDto[];
    marketingAreaTypes?: CreateOrUpdatePartyMarketingAreaTypeDto[];
    cooperationAreaTypes?: CreateOrUpdatePartyCooperationAreaTypeDto[];
    isServiceAgreementNotNeeded: boolean;
    isUboNotNeeded: boolean;
    riskFactors?: CreateOrUpdatePartyRiskFactorDto[];
    entityFieldChangeReasons: CreateEntityFieldChangeReasonDto[] | null;
    partyGroups?: NumericIdPrimKeyDto[];
    blocks?: PartyBlockWriteDto[] | null;
}

export interface ContactDataCreateOrUpdatePersonDto extends ContactCreateOrUpdateDto {
    personalCode: string | null;
    personTitleCode: string | null;
    personTitleText: string | null;
    linkedinUrl: string | null;
    homeAddress: AddressDto | null;
    homeLocalAddress: AddressDto | null;
    correspondenceAddress: AddressDto | null;
    firstName: string;
    lastName: string;
    localFirstName: string | null;
    localLastName: string | null;
    position: string | null;
    isPoliticallyExposedPerson: boolean;
    identityDocumentNumber: string | null;
    identityDocumentDateOfIssue: string | null;
    identityDocumentDateOfExpiry: string | null;
    identityDocumentIssuingCountryCode: string | null;
    identityDocumentIssuingAuthority: string | null;
    /** Omitted, the stored list is kept; empty, it is cleared. */
    areaOfResponsibilityTypes?: CreateOrUpdatePartyAreaOfResponsibilityTypeDto[];
}

export interface ContactDataCreateOrUpdateCompanyDto extends ContactCreateOrUpdateDto {
    officialName: string | null;
    officialLocalName: string | null;
    regCode: string | null;
    isVatLiable: boolean | null;
    vatRegNum: string | null;
    vatCountryCode: string | null;
    website: string | null;
    einvoiceAddress: string | null;
    einvoiceOperatorCode: string | null;
    einvoiceOperatorType: EinvoiceOperatorType | null;
    registrationAddress: AddressDto | null;
    registrationLocalAddress: AddressDto | null;
    visitingAddress: AddressDto | null;
    correspondenceAddress: AddressDto | null;
}

export interface ContactForm {
    partyType: PartyType;
    relationshipType: RelationshipTypeCode;
    name: string;
    officialLocalName: string;
    position: string | null;
    mainEmail: string | null;
    mainPhone: string | null;
    mainMobile: string | null;
    statusCode: string;
    clientReference: string | null;
    preferredLanguage: { label: string; value: string } | null;
    billingNotes: string | null;
    invoicingInfo: string | null;
    debtNotes: string | null;
    debtExplanation: string | null;
    debtHistory: string | null;
    notes: string | null;
    coiNotes: string | null;
    employees: { name: string; id: number }[] | null;
    employedBy: { name: string; id: number }[] | null;
    primaryContact: { name: string; id: number } | null;
    primaryContactForCompanies: { name: string; id: number }[] | null;
    businessAreas: { label: string; value: string }[] | null;
    relatedKeywords: { label: string; value: string; notes?: string }[] | null;
    managers: { name: string; id: number }[] | null;
    partners: { name: string; id: number }[] | null;
    administrators: { name: string; id: number }[] | null;
    businessContacts: { name: string; id: number }[] | null;
    paymentNotificationReceivers: { name: string; id: number }[] | null;
    billingAddress: AddressDto | null;
    billingLocalAddress: AddressDto | null;
    personalCode: string | null;
    personTitle: { label: string; value: string } | null;
    personManualTitle: string | null;
    linkedinUrl: string | null;
    homeAddress: AddressDto | null;
    homeLocalAddress: AddressDto | null;
    firstName: string;
    lastName: string;
    localFirstName: string | null;
    localLastName: string | null;
    officialName: string | null;
    regCode: string | null;
    notVatLiable: boolean;
    vatRegNum: string | null;
    vatCountryCode: { label: string; value: string } | null;
    website: string | null;
    einvoiceAddress: string | null;
    einvoiceOperatorCode: string | null;
    einvoiceOperatorType: { label: string; value: string } | null;
    registrationAddress: AddressDto | null;
    registrationLocalAddress: AddressDto | null;
    visitingAddress: AddressDto | null;
    correspondenceAddress: AddressDto | null;
    segmentType: { label: string; value: string } | null;
    serviceLevelType: { label: string; value: string } | null;
    payerParty: { name: string; id: number } | null;
    useJointInvoicing: boolean;
    isInternalClient: boolean;
    issueInvoicesWithCurrentDate: boolean;
    primaryRecipientParty: NamedEntityMinDto | null;
    primaryRecipientAdditionalEmails: string;
    secondaryRecipientParties: NamedEntityMinDto[];
    secondaryRecipientAdditionalEmails: string;
    invoiceArchiveEmail: string;
    invoiceTemplate: { id: number } | null;
    invoiceSpecTemplate: { id: number } | null;
    isSpecIncludedInInvoice: { value: boolean; label: string } | null;
    invoiceCustomization: CustomizationDto | null;
    dmsLink: string | null;
    isPoliticallyExposedPerson: boolean;
    identityDocumentNumber: string | null;
    identityDocumentDateOfIssue: string | null;
    identityDocumentDateOfExpiry: string | null;
    identityDocumentIssuingCountryCode: { label: string; value: string } | null;
    identityDocumentIssuingAuthority: string | null;
    invoicingDeadlineDay: { id: number; name: string } | null;
    invoicingPaymentDays: number | null;
    preferredBankAccount: { value: number; label: string; group: string } | null;
    externalBillingInfo: string | null;
    isPersonaNonGrata: boolean;
    riskProfileTypeCode: { label: string; value: string } | null;
    riskProfileNotes: string | null;
    complianceVerificationStatusTypeCode: { label: string; value: string } | null;
    complianceVerificationNotes: string | null;
    preferredMailingLanguageTypeCode?: { label: string; value: string } | null;
    countries?: { label: string; value: string }[];
    mailingPreferences?: { label: string; value: string }[];
    marketingAreaTypes?: { label: string; value: string }[];
    areaOfResponsibilityTypes?: { label: string; value: string }[];
    cooperationAreaTypes?: { label: string; value: string }[];
    isServiceAgreementNotNeeded: boolean;
    isUboNotNeeded: boolean;
    riskFactors?: { label: string; value: string }[];
    changeReasons: Record<string, string> | null;
    partyGroups: PartyGroupMin[];
    blocking: PartyBlockingFormState;
}

export interface PartyCountryDto {
    id: number;
    countryTypeCode: string;
}

interface CreateOrUpdatePartyCountryDto {
    countryTypeCode: string;
}

export interface PartyMailingPreferenceDto {
    id: number;
    mailingPreferenceTypeCode: string;
}

interface CreateOrUpdatePartyMailingPreferenceDto {
    mailingPreferenceTypeCode: string;
}

export interface PartyMarketingAreaTypeDto {
    id: number;
    marketingAreaTypeCode: string;
}

interface CreateOrUpdatePartyMarketingAreaTypeDto {
    marketingAreaTypeCode: string;
}

interface PartyAreaOfResponsibilityTypeDto {
    id: number;
    areaOfResponsibilityTypeCode: string;
}

interface CreateOrUpdatePartyAreaOfResponsibilityTypeDto {
    areaOfResponsibilityTypeCode: string;
}

interface CreateOrUpdatePartyRiskFactorDto {
    riskFactorTypeCode: string;
}

export interface PartyCooperationAreaTypeDto {
    id: number;
    cooperationAreaTypeCode: string;
}

interface CreateOrUpdatePartyCooperationAreaTypeDto {
    cooperationAreaTypeCode: string;
}

export interface FuzzySearchResultDto extends ContactMinDto {
    similarity: number;
    searchField: string;
    personalCode?: string | null;
    regCode?: string | null;
    mainPhone?: string | null;
    mainMobile?: string | null;
    firstName?: string;
    lastName?: string;
    billingAddress?: AddressDto | null;
    billingNotes?: string | null;
    invoicingInfo?: string | null;
    createdAt?: string;
    lastModifiedAt?: string;
    homeAddress?: AddressDto | null;
    registrationAddress?: AddressDto | null;
    visitingAddress?: AddressDto | null;
}

export interface UpdatePartyEmailDto {
    id: number;
    mainEmail?: string | null;
}

export interface UpdatePartyMobileDto {
    id: number;
    mainMobile?: string | null;
}

export interface UpdatePartyAddressDto extends AddressDto {
    id: number;
    addressType?: string;
}
